import  { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginPopUP.css";
import { StoreContext } from "../Context/StoreContext";
import axios from "axios";
import { StoreContextType } from "../Context/types";

interface LoginPopUPProps {
  setShowLogin: React.Dispatch<React.SetStateAction<boolean>>;
}

type ResetStep = "request" | "verify";

export const LoginPopUP = ({ setShowLogin } : LoginPopUPProps) => {
  const navigate = useNavigate();
  const { url, setToken } = useContext(StoreContext) as StoreContextType;

  const [data, setData] = useState({
    email: "",
    password: "",
  });

  const [mode, setMode] = useState<"login" | "reset">("login");
  const [resetStep, setResetStep] = useState<ResetStep>("request");
  const [resetData, setResetData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onChangeHandler = (event : React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onResetChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setResetData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onLogin = async (event : React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Basic validation
    if (!data.email || !data.password) {
      setError("Please fill in all fields.");
      return;
    }

    const newUrl = `${url}/api/data/login`;

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(newUrl, data);
      if (response.data.success) {
        setToken(response.data.token);
        localStorage.setItem("token", response.data.token);
        setShowLogin(false);
        setData({ email: "", password: "" });
      } else {
        setError(response.data.message);
      }
    } catch (error:any) {
      console.error("Login error:", error);
      if (error.response) {
        setError(error.response.data.message || "An error occurred. Please try again.");
      } else {
        setError("Network error. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const switchToRegister = () => {
    setShowLogin(false);
    navigate('/register');
  };

  const openResetFlow = () => {
    setMode("reset");
    setResetStep("request");
    setError("");
    setSuccessMessage("");
  };

  const backToLogin = () => {
    setMode("login");
    setError("");
    setSuccessMessage("");
  };

  const onRequestResetOtp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!resetData.email) {
      setError("Please enter your email.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${url}/request-reset`, { email: resetData.email });
      if (response.data.success) {
        setResetStep("verify");
        setSuccessMessage(response.data.message || "OTP sent to your email.");
      } else {
        setError(response.data.message || "Unable to send OTP.");
      }
    } catch (requestError: any) {
      console.error("Request reset error:", requestError);
      setError(requestError.response?.data?.message || "Network error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const onResetPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!resetData.email || !resetData.otp || !resetData.newPassword || !resetData.confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (!/^\d{6}$/.test(resetData.otp)) {
      setError("OTP must be a 6-digit number.");
      return;
    }

    if (resetData.newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (resetData.newPassword !== resetData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${url}/reset-password`, {
        email: resetData.email,
        otp: resetData.otp,
        newPassword: resetData.newPassword,
      });

      if (response.data.success) {
        setShowLogin(false);
        setMode("login");
        setResetStep("request");
        setData({ email: "", password: "" });
        setResetData({ email: "", otp: "", newPassword: "", confirmPassword: "" });
      } else {
        setError(response.data.message || "Unable to reset password.");
      }
    } catch (resetError: any) {
      console.error("Reset password error:", resetError);
      setError(resetError.response?.data?.message || "Network error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = loading ? "Processing..." : mode === "login" ? "Login" : resetStep === "request" ? "Send OTP" : "Reset Password";

  return (
    <div className="login-popup">
      <form onSubmit={mode === "login" ? onLogin : resetStep === "request" ? onRequestResetOtp : onResetPassword} className="login-popup-container">
        {/* Close Button - Top Right */}
        <button
          type="button"
          onClick={() => {
            setShowLogin(false);
            setMode("login");
            setResetStep("request");
            setError("");
            setSuccessMessage("");
          }}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full border-2 border-gray-800 hover:bg-gray-100 transition-colors z-10"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Orange Bar */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-orange-500 rounded-t-xl"></div>

        {/* Title */}
        <div className="text-center mb-6 mt-4">
          <h2 className="text-3xl font-bold mb-2">{mode === "login" ? "Login" : resetStep === "request" ? "Forgot Password" : "Reset Password"}</h2>
          <p className="text-gray-600 text-sm">
            {mode === "login"
              ? "Please fill in the fields below:"
              : resetStep === "request"
                ? "Enter your email to receive an OTP:"
                : "Enter the OTP and your new password:"}
          </p>
        </div>

        {/* Input Fields */}
        <div className="space-y-4">
          {mode === "login" ? (
            <>
              <div>
                <input
                  name="email"
                  onChange={onChangeHandler}
                  value={data.email}
                  type="email"
                  placeholder="Email Id"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
              <div>
                <input
                  name="password"
                  onChange={onChangeHandler}
                  value={data.password}
                  type="password"
                  placeholder="Password"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <input
                  name="email"
                  onChange={onResetChangeHandler}
                  value={resetData.email}
                  type="email"
                  placeholder="Email Id"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                />
              </div>
              {resetStep === "verify" && (
                <>
                  <div>
                    <input
                      name="otp"
                      onChange={onResetChangeHandler}
                      value={resetData.otp}
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit OTP"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      name="newPassword"
                      onChange={onResetChangeHandler}
                      value={resetData.newPassword}
                      type="password"
                      placeholder="New Password"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      name="confirmPassword"
                      onChange={onResetChangeHandler}
                      value={resetData.confirmPassword}
                      type="password"
                      placeholder="Confirm New Password"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {mode === "login" ? (
          <div className="text-left mt-2">
            <button type="button" onClick={openResetFlow} className="text-sm text-gray-700 hover:underline">
              Forgot Password ?
            </button>
          </div>
        ) : (
          <div className="text-left mt-2">
            <button type="button" onClick={backToLogin} className="text-sm text-gray-700 hover:underline">
              Back to Login
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && <p className="text-red-600 text-sm text-center mt-4">{error}</p>}

        {successMessage && <p className="text-green-700 text-sm text-center mt-4">{successMessage}</p>}

        {/* Login Button */}
        <button 
          type="submit" 
          disabled={loading}
          className="w-full bg-gray-800 text-white py-3 rounded-lg font-medium text-sm hover:bg-gray-900 transition-colors mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitLabel}
        </button>

        {/* Toggle to Register */}
        <div className="text-center mt-4">
          <p className="text-gray-700 text-sm">
            New customer?{" "}
            <button 
              type="button"
              onClick={switchToRegister}
              className="font-medium hover:underline text-gray-900"
            >
              Create an account
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};
